// The single-pill sphere header (type avatar + caret, urgency, privacy lock, name) shared
// by the Sphere Editor (SphereProperties) and the Add Sphere dialog so both stay identical.
// Presentational / Formik-agnostic: consumers pass plain value + onChange. A right-side
// `children` slot carries flow controls (e.g. ArcStageSelector) inside the pill.
import React, { useEffect, useRef, useState } from 'react';
import SphereTypeAvatar from '../SphereTypeAvatar/SphereTypeAvatar';
import UrgencyIcon from '../../atoms/urgency/urgency';
import UrgencyPillMolecule from '../UrgencyPill/UrgencyPill';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import type { Sphere } from 'types/global';
import './SphereHeaderRowMolecule.scss';

/** One Archetype shown as a chip after the sphere's name. */
export interface SphereHeaderArchetype {
  key: string;
  label: string;
  glyph?: string;
  isPrimary: boolean;
  unavailable?: boolean;
}

/** Chips shown before the rest collapse into "+N". */
const HEADER_ARCHETYPE_CHIP_LIMIT = 3;

// Presentational: chips only, never a loading or error state — the header does not wait on Archetypes.
const HeaderArchetypes: React.FC<{ archetypes: SphereHeaderArchetype[] }> = ({ archetypes }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const overflowRef = useRef<HTMLSpanElement>(null);

  // A disclosure has to close when the reader moves on: Escape while the trigger holds focus is
  // only half of it, and a list left hanging over the header after a click elsewhere reads as a
  // stuck panel. Same mousedown-outside idiom as ContentSharingDropdownMolecule / NotificationBell.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);
  // Primary first, otherwise the order the caller gave.
  const ordered = [...archetypes].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  const shown = ordered.slice(0, HEADER_ARCHETYPE_CHIP_LIMIT);
  const hiddenWide = ordered.length - shown.length;
  // At narrow widths only the primary chip stays, so the count of "the rest" is larger.
  const hiddenNarrow = ordered.length - 1;
  const chipName = (a: SphereHeaderArchetype) => {
    if (a.unavailable) return t('archetypes.chip.unavailableName', { name: a.label });
    if (a.isPrimary) return t('archetypes.chip.primaryName', { name: a.label });
    return a.label;
  };

  return (
    <div className="archetype-chips" data-datum="archetype-chips">
      {shown.map((a, index) => (
        <span
          key={a.key}
          data-testid="archetype-chip"
          className={`archetype-chips__chip${a.isPrimary ? ' archetype-chips__chip--primary' : ''}${index > 0 ? ' archetype-chips__chip--secondary' : ''}`}
          title={chipName(a)}
          aria-label={chipName(a)}
        >
          <ChipAtom
            label={a.label}
            icon={a.glyph}
            unavailable={a.unavailable}
            unavailableReason={t('archetypes.chip.unavailable')}
          />
        </span>
      ))}
      {hiddenNarrow > 0 && (
        <span className="archetype-chips__overflow-wrap" ref={overflowRef}>
          <button
            type="button"
            className={`archetype-chips__overflow${hiddenWide === 0 ? ' archetype-chips__overflow--narrow-only' : ''}`}
            aria-expanded={open}
            aria-label={t('archetypes.header.overflowLabel', {
              count: hiddenNarrow,
              names: ordered.slice(1).map((a) => a.label).join(', '),
            })}
            onClick={() => setOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
          >
            {hiddenWide > 0 && <span className="archetype-chips__count--wide">{`+${hiddenWide}`}</span>}
            <span className="archetype-chips__count--narrow">{`+${hiddenNarrow}`}</span>
          </button>
          {open && (
            <ul className="archetype-chips__list" aria-label={t('archetypes.header.overflowTitle')}>
              {ordered.slice(1).map((a) => (
                <li key={a.key}>{chipName(a)}</li>
              ))}
            </ul>
          )}
        </span>
      )}
    </div>
  );
};

// Privacy lock (extracted from SphereProperties so both surfaces render it identically).
interface PrivacyLockIconProps {
  editable?: boolean;
  isPrivate: boolean;
  hasPermission: boolean;
  onToggle: () => void;
  fontSize?: string;
}

const PrivacyLockIcon: React.FC<PrivacyLockIconProps> = ({
  editable,
  isPrivate,
  hasPermission,
  onToggle,
  fontSize = '1rem',
}) => {
  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!hasPermission) return;
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(e);
    }
  };

  if (editable) {
    const label = isPrivate
      ? 'Private sphere - click to make visible'
      : 'Public sphere - click to make private';
    return (
      <i
        className={`pi ${isPrivate ? 'pi-lock' : 'pi-lock-open'} privacy-lock-icon`}
        style={{
          marginRight: '8px',
          color: 'var(--primary-color, #3B82F6)',
          cursor: hasPermission ? 'pointer' : 'not-allowed',
          opacity: hasPermission ? 1 : 0.5,
          fontSize,
        }}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={hasPermission ? 0 : -1}
        aria-label={label}
        title={label}
      />
    );
  }

  if (isPrivate) {
    return (
      <i
        className="pi pi-lock privacy-lock-icon"
        style={{ marginRight: '8px', color: 'var(--primary-color, #3B82F6)' }}
      />
    );
  }

  return null;
};

export interface SphereHeaderRowMoleculeProps {
  /** Sphere providing the type glyph + glyph color for the avatar. */
  sphere: Sphere;
  /** When false the avatar/urgency/name render read-only (no caret, no editing). */
  editable?: boolean;
  /** Tooltip/title for the type avatar (typically the schema name). */
  typeTitle?: string;
  /** Click handler for the type avatar — opens the type selector / returns to type choice. */
  onTypeClick?: () => void;
  /**
   * False for an existing sphere: its type cannot change, so the avatar shows no caret and
   * offers no selector. Defaults to true, so new-sphere consumers are unchanged.
   */
  typeChangeable?: boolean;
  /** The sphere's Archetypes; up to three chips (primary first), the rest collapse to a focusable "+N". */
  archetypes?: SphereHeaderArchetype[];

  name: string;
  onNameChange: (name: string) => void;
  onNameBlur?: () => void;
  namePlaceholder?: string;
  nameInvalid?: boolean;

  /** Show the urgency control (hidden when the schema has no urgency). */
  showUrgency?: boolean;
  urgencyValue?: number;
  onUrgencyChange?: (urgency: number) => void;

  /** Show the privacy lock (hidden when the schema has no is_private). */
  showPrivacy?: boolean;
  isPrivate?: boolean;
  onPrivacyToggle?: () => void;
  hasManageSecurityPermission?: boolean;

  /** Custom visual control (e.g. glyph/picture editor) replaces the type avatar when set. */
  visualControl?: React.ReactNode;

  /** Right-aligned slot inside the pill (e.g. flow controls). */
  children?: React.ReactNode;
  className?: string;
}

const SphereHeaderRowMolecule: React.FC<SphereHeaderRowMoleculeProps> = ({
  sphere,
  editable = true,
  typeTitle,
  onTypeClick,
  typeChangeable = true,
  archetypes,
  name,
  onNameChange,
  onNameBlur,
  namePlaceholder,
  nameInvalid = false,
  showUrgency = false,
  urgencyValue = 3,
  onUrgencyChange,
  showPrivacy = false,
  isPrivate = false,
  onPrivacyToggle,
  hasManageSecurityPermission = false,
  visualControl,
  children,
  className = '',
}) => {
  const [isUrgencyOpen, setIsUrgencyOpen] = useState(false);

  let typeControl: React.ReactNode;
  if (editable && typeChangeable) {
    typeControl = <SphereTypeAvatar sphere={sphere} onClick={onTypeClick} title={typeTitle} />;
  } else if (visualControl && !typeChangeable) {
    typeControl = visualControl;
  } else {
    typeControl = (
      <div className="sphere-type-label">
        <SphereTypeAvatar sphere={sphere} title={typeTitle} editable={false} />
      </div>
    );
  }

  return (
    <div className={`cp-sphere-header ${className}`.trim()}>
      {typeControl}

      {showUrgency && (
        <div className="urgency-container">
          {editable ? (
            <>
              <button
                type="button"
                className={`urgency-button ${isUrgencyOpen ? 'expanded' : 'collapsed'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUrgencyOpen((prev) => !prev);
                }}
                aria-label="Toggle Urgency"
                aria-expanded={isUrgencyOpen}
              >
                <UrgencyIcon urgencyValue={urgencyValue} />
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none">
                  <path
                    d="M7.82191 8.66505C7.74441 8.7659 7.62667 8.85053 7.48166 8.90963C7.33663 8.96873 7.16996 9 6.99999 9C6.83002 9 6.66336 8.96873 6.51834 8.90963C6.37331 8.85053 6.25557 8.7659 6.17807 8.66505L1.09879 1.89272C1.02795 1.79901 0.99422 1.69478 1.00081 1.58987C1.0074 1.48502 1.05408 1.38307 1.13644 1.29361C1.21879 1.20416 1.33407 1.13021 1.47133 1.07887C1.60859 1.02746 1.76329 1.00031 1.92071 1H12.0793C12.2367 1.00031 12.3913 1.02746 12.5287 1.07887C12.6659 1.13021 12.7812 1.20416 12.8635 1.29361C12.9459 1.38307 12.9925 1.48502 12.9992 1.58987C13.0058 1.69478 12.972 1.79901 12.9012 1.89272L7.82191 8.66505Z"
                    stroke="#26A3E3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isUrgencyOpen && (
                <UrgencyPillMolecule
                  placement="popover"
                  urgencyValue={urgencyValue}
                  onUrgencyChange={(u: string) => {
                    onUrgencyChange?.(Number(u));
                    setIsUrgencyOpen(false);
                  }}
                />
              )}
            </>
          ) : (
            <span className="urgency-view">
              <UrgencyIcon urgencyValue={urgencyValue} />
            </span>
          )}
        </div>
      )}

      {showPrivacy && (
        <div className="privacy-container">
          <PrivacyLockIcon
            editable={editable}
            isPrivate={isPrivate}
            hasPermission={hasManageSecurityPermission}
            onToggle={() => onPrivacyToggle?.()}
            fontSize="1.125rem"
          />
        </div>
      )}

      <div className="sphere-name-container">
        <input
          type="text"
          className={`sphere-name-input ${nameInvalid ? 'is-invalid' : ''}`.trim()}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={onNameBlur}
          placeholder={namePlaceholder}
          disabled={!editable}
        />
      </div>

      {archetypes && archetypes.length > 0 && <HeaderArchetypes archetypes={archetypes} />}

      {children && <div className="flow-components-container">{children}</div>}
    </div>
  );
};

export default SphereHeaderRowMolecule;
